import React, { ReactNode } from 'react';
import TranslationComponent from '../../../components/lesson/text/Translation';
import { EntityFields, EntityStringId, IEntityType, IEntity } from '../../../types/entity';
import EntityTranslation, { Fallback } from '../../entities/EntityTranslation';
import { EntityTranslationSharedSelection } from 'app/components/call/SharedSelection';
import { Language } from 'app/constants/enums/Language';

export { Fallback };

interface IProps<T extends IEntity> {
  children?: (params: { translation: string, language?: Language }) => ReactNode;
  fallback?: Fallback;
  field: keyof EntityFields<T>;
  id: EntityStringId;
  type: IEntityType<T>;
}

/**
 * Wraps the result in `<WithFontsForLanguage>` for the TextTranslation entity's language.
 *
 * If `children` is not given, defaults to:
 *
 * ```
 * <Translation id={id} type={type} field={field}>{({ translation }) =>
 *   <FormattedText variant={TRANSLATION_VARIANT} text={translation} />;
 * }</Translation>
 * ```
 *
 * `TRANSLATION_VARIANT` provides default markup formatting for translations (particularly, makes
 * translations support furigana markup by default).
 *
 * `children` should only be used if you need access to the raw string, for example to add markup
 * formatting.
 */
const Translation = <T extends IEntity>({ fallback, children, id, field, type }: IProps<T>) => (
  <EntityTranslation
    fallback={fallback}
    id={id}
    field={field}
    type={type}
  >
    {({ language, translation }) => {
      const content = <TranslationComponent children={children} translation={translation} language={language} />;

      return (
        language
        ? (
          <EntityTranslationSharedSelection type={type} id={id} field={field} language={language} inline>
            {content}
          </EntityTranslationSharedSelection>
        )
        : content
      );
    }}
  </EntityTranslation>
);

export default Translation;
